import React, { FC, useMemo } from 'react';

/* eslint-disable prettier/prettier */

import { Box, Button, CircularProgress, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { CheckCircle, ClockCounterClockwise, WarningCircle } from 'phosphor-react';

import { useTransactionProvider } from '~components/providers/TransactionProvider';
import { TxInstance, TxStatus } from '~components/providers/types';
import { NormalLink } from '~components/shared/link';
import { thinScrollBarSx } from '~components/shared/scroll/ThinScroll';
import AppColors, { primaryColors, secondaryColors, statusRed } from '~components/theme/styles/colors';
import { columnGap } from '~components/theme/styles/mixins';

import { AccountPanel } from './accountPanel';

const TransactionItem: FC<{ tx: TxInstance & { timestampExplicit?: string } }> = ({
  tx: { key, error, complete, url, timestampExplicit, isTransacting, status },
}) => {
  const txName = key.replaceAll('-', ' ');

  return (
    <Stack direction={'column'} sx={{ gap: '4px' }}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Typography textTransform={'uppercase'} variant={'label3'}>
          {key.length > 18 ? `${txName.substring(0, 18)}...` : txName}
        </Typography>
        <Stack direction={'row'} alignItems={'center'}>
          {error && (
            <Box sx={{ height: '22px' }}>
              <WarningCircle size={22} color={statusRed[100]} />
            </Box>
          )}
          {complete && !error && (
            <Box sx={{ height: '22px' }}>
              <CheckCircle size={22} color={secondaryColors[200]} />
            </Box>
          )}
        </Stack>
      </Stack>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        {!timestampExplicit ? (
          <Stack direction={'row'} alignItems={'center'} spacing={0.5} py={0.5}>
            {isTransacting && <CircularProgress color={'secondary'} size={15} />}
          </Stack>
        ) : (
          <Typography variant={'label3'} color={primaryColors[700]}>
            {timestampExplicit}
          </Typography>
        )}
        {url && complete === true ? (
          <NormalLink href={url ?? ''} target="_blank">
            <Typography variant="label3">VIEW TX</Typography>
          </NormalLink>
        ) : complete && error ? (
          <Tooltip
            placement="top"
            sx={{ maxWidth: 200 }}
            title={
              <Box sx={{ padding: '8px', ...columnGap(16) }}>
                <Stack direction={'column'} spacing={0.5}>
                  <Typography variant={'label3'} color={primaryColors[400]}>
                    Action:
                  </Typography>
                  <Typography variant={'label3'}>{txName}</Typography>
                </Stack>
                {url ? (
                  <Stack direction={'column'} spacing={0.5}>
                    <Typography variant={'label3'} color={primaryColors[400]}>
                      TX URL:
                    </Typography>
                    <NormalLink href={url} target={'_blank'}>
                      <Typography variant={'label3'}>View in explorer</Typography>
                    </NormalLink>
                  </Stack>
                ) : null}
                <Stack direction={'column'} spacing={0.5}>
                  <Typography variant={'label3'} color={primaryColors[400]}>
                    Message:
                  </Typography>
                  <Typography variant={'label3'}>{error}</Typography>
                </Stack>
              </Box>
            }
          >
            <Typography variant={'label3'} sx={{ color: primaryColors[400], fontWeight: 500 }}>
              VIEW ERROR
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant={'label3'} color={AppColors.neutralGrey[3]} sx={{ fontWeight: 500 }}>
            {status === 'confirming' ? 'Waiting for confirmation' : 'Transacting'}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export const AccountDialTransactions: FC<{ forceOpen?: boolean }> = ({ forceOpen }) => {
  const { transactions, history, clearHistory } = useTransactionProvider();

  const activeTxs = useMemo(
    () =>
      Object.values(transactions).filter(
        ({ isTransacting, status }) =>
          isTransacting ||
          !(status === TxStatus.idle || status === TxStatus.error || status === TxStatus.success),
      ),
    [transactions],
  );

  return (
    <AccountPanel
      titleContent={'Transactions'}
      titleIcon={<ClockCounterClockwise size={22} color={primaryColors[400]} />}
      forceOpen={forceOpen}
      panelBodySx={{
        maxHeight: '424px',
        ...thinScrollBarSx,
      }}
    >
      {history.length > 0 || activeTxs.length > 0 ? (
        <>
          {!!activeTxs.length && (
            <Box mb={2}>
              <Typography variant={'label3'} color={primaryColors[900]}>
                ACTIVE TRANSACTIONS
              </Typography>
              {activeTxs.map((tx, index) => (
                <Box sx={{ width: '100%' }} key={index}>
                  <TransactionItem tx={tx} />
                </Box>
              ))}
              <Divider sx={{ my: 2 }} />
            </Box>
          )}
          <Stack
            mt={2}
            sx={{ width: '100%' }}
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            mb={2}
          >
            <Typography variant={'label3'} color={primaryColors[900]}>
              TRANSACTION HISTORY
            </Typography>
            <Button
              variant="outlined"
              sx={{
                minWidth: 0,
                padding: 0,
              }}
              disabled={history.length <= 0}
              onClick={e => {
                clearHistory();
                e.stopPropagation();
              }}
            >
              <Typography variant="label3" sx={{ color: primaryColors[700] }}>
                Clear
              </Typography>
            </Button>
          </Stack>
          {history.map((tx, index) => (
            <Box width="100%" key={index}>
              {index !== 0 && <Divider sx={{ my: 2 }} />}
              <TransactionItem tx={tx} />
            </Box>
          ))}
        </>
      ) : (
        <Typography variant={'body2'} textAlign={'center'} my={1}>
          No transactions to show.
        </Typography>
      )}
    </AccountPanel>
  );
};
